// ═══════════════════════════════════════════════════════════════════════════════
// Interactive OpenStreetMap.org official embed + attribution / Google Maps link.
// ═══════════════════════════════════════════════════════════════════════════════

import type { ReactNode } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { buildGoogleMapsUrl, buildOsmEmbedPreviewUrl } from '../../lib/osmStaticMap';

type OsmEmbedInteractiveMapProps = {
  lat: number;
  lng: number;
  /** Shown between the map iframe and the Google/OSM footer links */
  children?: ReactNode;
};

export const OsmEmbedInteractiveMap = ({ lat, lng, children }: OsmEmbedInteractiveMapProps) => {
  const { t } = useLanguage();

  return (
    <div className="space-y-3">
      <div className="relative aspect-video min-h-[13rem] w-full overflow-hidden rounded-lg bg-gray-100 dark:bg-gray-800">
        <iframe
          title={t('task.review.gpsStaticMapAlt')}
          src={buildOsmEmbedPreviewUrl(lat, lng)}
          className="h-full w-full border-0"
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
      </div>
      {children}
      <p className="flex flex-wrap items-center gap-x-1 text-xs text-gray-500 dark:text-gray-400">
        <a
          href={buildGoogleMapsUrl(lat, lng)}
          target="_blank"
          rel="noopener noreferrer"
          tabIndex={0}
          className="text-primary-600 underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-primary-400 rounded dark:text-primary-400"
          aria-label={t('task.review.openInGoogleMapsAria')}
        >
          {t('task.review.openInGoogleMaps')}
        </a>
        <span aria-hidden="true">·</span>
        <a
          href="https://www.openstreetmap.org/copyright"
          target="_blank"
          rel="noopener noreferrer"
          tabIndex={0}
          className="underline hover:no-underline focus:outline-none focus:ring-2 focus:ring-primary-400 rounded"
        >
          {t('task.review.osmCopyright')}
        </a>
      </p>
    </div>
  );
};
